import { useState } from "react";

export interface ProvenancePin {
  readonly label: string;
  /** The value this view was pinned to. Immutable for the life of the analysis. */
  readonly value: string;
  readonly digest?: string | undefined;
  /**
   * The equivalent value now, where one exists. Supplying it lets the drawer say that the
   * portfolio moved after the analysis ran, rather than leaving the reader to compare ids.
   */
  readonly current?: string | undefined;
}

export interface ProvenanceDrawerProps {
  readonly title?: string;
  readonly pins: ProvenancePin[];
  /** Stated in the drawer, because a pin that has moved is provenance, not a side note. */
  readonly movedExplanation?: string | undefined;
  /** Opens on mount, so a link can point straight at the record. */
  readonly defaultOpen?: boolean | undefined;
  readonly triggerLabel?: string | undefined;
}

/**
 * Abbreviates opaque identifiers only. A value a person can read — a profile name, a release
 * version — is left whole, because truncating it hides the very thing the drawer exists to show.
 */
function shorten(value: string): string {
  const opaque = /^[0-9a-zA-Z._:@-]+$/.test(value) && !value.includes(" ");
  return opaque && value.length > 24 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value;
}

export function ProvenanceDrawer({
  title = "Source details",
  pins,
  movedExplanation,
  defaultOpen = false,
  triggerLabel = "View source details",
}: ProvenanceDrawerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState<string | undefined>(undefined);

  const movedPins = pins.filter(
    (pin) => pin.current !== undefined && pin.current.length > 0 && pin.current !== pin.value,
  );

  async function copy(label: string, value: string) {
    try {
      await navigator.clipboard?.writeText(value);
      setCopied(label);
    } catch {
      // Copying is a convenience; the value stays selectable on screen either way.
      setCopied(undefined);
    }
  }

  return (
    <>
      <button className="button provenance-trigger" type="button" onClick={() => setOpen(true)}>
        {triggerLabel}
      </button>
      {open ? (
        <div className="drawer-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <aside
            className="provenance-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="provenance-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p className="eyebrow">Where this result came from</p>
                <h2 id="provenance-title">{title}</h2>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close source details"
              >
                Close
              </button>
            </header>
            <p>
              These are the sources and saved versions used for this result. This record is also
              called provenance.
            </p>

            {movedPins.length > 0 || movedExplanation !== undefined ? (
              <p className="provenance-moved" role="status">
                This analysis still reads the versions it ran against.{" "}
                {movedExplanation ??
                  `${movedPins.length} pinned input has moved since, so the current portfolio and this result no longer describe the same state.`}
              </p>
            ) : null}

            <dl className="provenance-pins">
              {pins.map((pin) => {
                const moved =
                  pin.current !== undefined && pin.current.length > 0 && pin.current !== pin.value;
                return (
                  <div key={`${pin.label}:${pin.value}`}>
                    <dt>{pin.label}</dt>
                    <dd>
                      <span className="provenance-value" title={pin.value}>
                        {shorten(pin.value)}
                      </span>
                      {moved ? <span className="provenance-tag">MOVED</span> : null}
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => void copy(pin.label, pin.digest ?? pin.value)}
                      >
                        {copied === pin.label ? "Copied" : "Copy"}
                      </button>
                    </dd>
                    {pin.digest === undefined ? null : (
                      <code title={pin.digest}>{shorten(pin.digest)}</code>
                    )}
                    {moved ? (
                      <p className="provenance-current">
                        Current: <span title={pin.current}>{shorten(pin.current!)}</span>
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </dl>

            <p>
              These references identify the governed inputs used for this view. They do not expose
              credentials or protected source content.
            </p>
          </aside>
        </div>
      ) : null}
    </>
  );
}
